/**
 * The single owner of the ADMINISTRATOR session token.
 *
 * Kept apart from `tokenStorage.ts` on purpose: the administrator is not a user,
 * and the two tokens must never be mistaken for each other - not by the API
 * client, and not by whoever reads this code. Signing out of one leaves the
 * other alone.
 *
 * STORAGE DECISION: `sessionStorage`, not `localStorage`. An admin session is
 * the most powerful credential this app issues, so it ends when the tab closes
 * rather than lingering for the next person at the machine. A refresh keeps it;
 * a new tab asks again. The server holds sessions in memory, so a restart signs
 * the administrator out regardless.
 *
 * Mirrored in memory so a privacy mode that throws on storage still works for
 * the life of the tab.
 */

const STORAGE_KEY = 'orbit-sync.admin-token';

type TokenListener = (token: string | null) => void;

const listeners = new Set<TokenListener>();

let cached: string | null = null;
let loaded = false;

function read(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function write(token: string | null): void {
  try {
    if (token === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, token);
    }
  } catch {
    // Non-fatal: the in-memory copy carries the session for this tab.
  }
}

export function getAdminToken(): string | null {
  if (!loaded) {
    cached = read();
    loaded = true;
  }

  return cached;
}

export function setAdminToken(token: string | null): void {
  if (getAdminToken() === token) {
    return;
  }

  cached = token;
  loaded = true;
  write(token);

  for (const listener of listeners) {
    listener(token);
  }
}

export function clearAdminToken(): void {
  setAdminToken(null);
}

/** Notifies when the token changes - including the API client dropping it after a 401. */
export function subscribeToAdminToken(listener: TokenListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
